import React, { useCallback, useMemo, type ChangeEvent } from 'react';
import { graphql, useFragment, useMutation } from 'react-relay';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { HelperMessage } from '@atlaskit/form';
import { Box, xcss } from '@atlaskit/primitives';
import { Radio } from '@atlaskit/radio';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { useShowErrorFlag } from '@atlassian/jira-personal-settings-error-flag/src/ui/index.tsx';
import { fireOperationalAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type {
	JiraEmailMimeType,
	mimeType_personalSettingsPreferenceFields_MimeTypePreferenceField$key,
} from '@atlassian/jira-relay/src/__generated__/mimeType_personalSettingsPreferenceFields_MimeTypePreferenceField.graphql';
import type { mimeType_personalSettingsPreferenceFields_MimeTypePreferenceFieldMutation } from '@atlassian/jira-relay/src/__generated__/mimeType_personalSettingsPreferenceFields_MimeTypePreferenceFieldMutation.graphql';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import messages from './messages.tsx';

type Option = {
	label: string;
	value: JiraEmailMimeType;
};

const MimeTypePreferenceField = ({
	mimeTypeFragment,
}: {
	mimeTypeFragment: mimeType_personalSettingsPreferenceFields_MimeTypePreferenceField$key;
}) => {
	const { formatMessage } = useIntl();
	const cloudId = useCloudId();
	const { showErrorFlag } = useShowErrorFlag();

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const data = useFragment(
		graphql`
			fragment mimeType_personalSettingsPreferenceFields_MimeTypePreferenceField on JiraNotificationOptions {
				emailMimeType @required(action: THROW)
				id @required(action: THROW)
				isEmailMimeTypeEditable @required(action: THROW)
			}
		`,
		mimeTypeFragment,
	);

	const [commit] =
		useMutation<mimeType_personalSettingsPreferenceFields_MimeTypePreferenceFieldMutation>(graphql`
			mutation mimeType_personalSettingsPreferenceFields_MimeTypePreferenceFieldMutation(
				$cloudId: ID!
				$input: JiraUpdateNotificationOptionsInput!
			) {
				jira {
					updateGlobalNotificationOptions(cloudId: $cloudId, input: $input)
						@optIn(to: "JiraNotificationPreference") {
						success
						errors {
							message
						}
						options {
							...mimeType_personalSettingsPreferenceFields_MimeTypePreferenceField
						}
					}
				}
			}
		`);
	const mimeTypeOptions: Option[] = useMemo(
		() => [
			{ label: 'HTML', value: 'HTML' },
			{ label: formatMessage(messages.optionText), value: 'TEXT' },
		],
		[formatMessage],
	);

	const selectedOption = mimeTypeOptions.find(
		(option: Option) => option.value === data.emailMimeType,
	);

	const handleChange = useCallback(
		(event: ChangeEvent<HTMLInputElement>) => {
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			const option = event.target.value as JiraEmailMimeType;
			if (option) {
				commit({
					variables: {
						cloudId,
						input: {
							emailMimeType: option,
						},
					},
					optimisticResponse: {
						jira: {
							updateGlobalNotificationOptions: {
								success: true,
								errors: [],
								options: {
									id: data.id,
									isEmailMimeTypeEditable: data.isEmailMimeTypeEditable,
									emailMimeType: option,
								},
							},
						},
					},
					onError: (error) => {
						showErrorFlag();
						fireErrorAnalytics({
							meta: {
								id: 'mutateMimeTypePreference',
								packageName: 'jiraPersonalSettingsPreferenceFields',
								teamName: 'jira-notifications-platform',
							},
							error,
							sendToPrivacyUnsafeSplunk: true,
						});
					},
					onCompleted: (response) => {
						const errors = response?.jira?.updateGlobalNotificationOptions?.errors;
						if (errors && errors?.length > 0) {
							showErrorFlag();
							errors.forEach((error) => {
								fireErrorAnalytics({
									meta: {
										id: 'mutateMimeTypePreferenceCompletedError',
										packageName: 'jiraPersonalSettingsPreferenceFields',
										teamName: 'jira-notifications-platform',
									},
									error: new Error(error?.message || 'Unknown Error'),
									sendToPrivacyUnsafeSplunk: true,
								});
							});
						} else {
							fireOperationalAnalytics(
								createAnalyticsEvent({}),
								'mutateMimeTypePreference success',
							);
						}
					},
				});
			}
		},
		[cloudId, commit, createAnalyticsEvent, data.id, data.isEmailMimeTypeEditable, showErrorFlag],
	);

	return (
		<>
			<Box xcss={radioBoxStyles}>
				<Radio
					value={mimeTypeOptions[0].value}
					label={mimeTypeOptions[0].label}
					name={mimeTypeOptions[0].value}
					testId="personal-settings-preference-fields.common.ui.mime-type.radio-html"
					isChecked={selectedOption === mimeTypeOptions[0]}
					onChange={handleChange}
				/>
			</Box>
			<Box>
				<Radio
					value={mimeTypeOptions[1].value}
					label={mimeTypeOptions[1].label}
					name={mimeTypeOptions[1].value}
					testId="personal-settings-preference-fields.common.ui.mime-type.radio-text"
					isChecked={selectedOption === mimeTypeOptions[1]}
					onChange={handleChange}
					isDisabled={!data.isEmailMimeTypeEditable}
				/>
			</Box>
			{!data.isEmailMimeTypeEditable && (
				<HelperMessage testId="personal-settings-preference-fields.common.ui.mime-type.helper-message">
					{formatMessage(messages.description)}
				</HelperMessage>
			)}
		</>
	);
};

const radioBoxStyles = xcss({
	marginBottom: 'space.100',
});

export default MimeTypePreferenceField;
